import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IAcademicWeekInitialState, IAcademicWeeksPayload } from "../types";
import {
    CreateAcademicWeek,
    DeleteAcademicWeeks,
    EditAcademicWeeks,
    GetAcademicWeeks,
} from "./thunk";

const initialState: IAcademicWeekInitialState = {
    academicWeeks: [],
    academicWeeksRestore: [],
    isLoadingAcademicWeeks: false,
    isViewAcademicWeeksRestore: false,
    selectedAcademicWeeksIDs: [],
};

const academicWeekSlice = createSlice({
    name: "academicWeeks",
    initialState,
    reducers: {
        setAcademicWeeks: (
            state,
            action: PayloadAction<IAcademicWeeksPayload[]>
        ) => {
            state.academicWeeks = action.payload;
        },

        setIsViewAcademicWeeksRestore: (state) => {
            state.isViewAcademicWeeksRestore =
                !state.isViewAcademicWeeksRestore;
        },

        deleteAcademicWeek: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            state.academicWeeks = state.academicWeeks.filter(
                (elem) => elem.id !== id
            );
        },

        updateAcademicWeek: (
            state,
            action: PayloadAction<{
                updatedAcademicWeek: IAcademicWeeksPayload;
            }>
        ) => {
            const { updatedAcademicWeek } = action.payload;
            state.academicWeeks = state.academicWeeks.map(
                (elem: IAcademicWeeksPayload) => {
                    if (elem.id === updatedAcademicWeek.id) {
                        return {
                            ...elem,
                            ...updatedAcademicWeek,
                        };
                    }
                    return elem;
                }
            );
        },

        setSelectedAcademicWeeksIDs: (
            state,
            action: PayloadAction<number[]>
        ) => {
            state.selectedAcademicWeeksIDs = action.payload;
        },

        addAcademicWeek: (
            state,
            action: PayloadAction<IAcademicWeeksPayload>
        ) => {
            state.academicWeeks.push(action.payload);
        },

        toggleAcademicWeekActive: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            const academicWeek = state.academicWeeks.find(
                (elem) => elem.id === id
            );
            if (academicWeek) {
                academicWeek.is_active = !academicWeek.is_active;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Get Academic Weeks
            .addCase(GetAcademicWeeks.pending, (state) => {
                state.isLoadingAcademicWeeks = true;
            })
            .addCase(GetAcademicWeeks.fulfilled, (state, action) => {
                state.isLoadingAcademicWeeks = false;
                state.academicWeeks = action.payload.filter(
                    (elem: IAcademicWeeksPayload) => elem.kind === "academic"
                );
            })
            .addCase(GetAcademicWeeks.rejected, (state) => {
                state.isLoadingAcademicWeeks = false;
            })
            // Create Academic Week
            .addCase(CreateAcademicWeek.pending, (state) => {
                state.isLoadingAcademicWeeks = true;
            })
            .addCase(CreateAcademicWeek.fulfilled, (state, action) => {
                state.isLoadingAcademicWeeks = false;
                state.academicWeeks.push(
                    ...action.payload.filter(
                        (elem: IAcademicWeeksPayload) =>
                            elem.kind === "academic"
                    )
                );
            })
            .addCase(CreateAcademicWeek.rejected, (state) => {
                state.isLoadingAcademicWeeks = false;
            })
            // Update Academic Week
            .addCase(EditAcademicWeeks.pending, (state) => {
                state.isLoadingAcademicWeeks = true;
            })
            .addCase(EditAcademicWeeks.fulfilled, (state, action) => {
                state.isLoadingAcademicWeeks = false;
                const index = state.academicWeeks.findIndex(
                    (week) => week.id === action.payload.id
                );
                if (index !== -1) {
                    state.academicWeeks[index] = action.payload;
                }
            })
            .addCase(EditAcademicWeeks.rejected, (state) => {
                state.isLoadingAcademicWeeks = false;
            })
            // Delete Academic Week
            .addCase(DeleteAcademicWeeks.pending, (state) => {
                state.isLoadingAcademicWeeks = true;
            })
            .addCase(DeleteAcademicWeeks.fulfilled, (state, action) => {
                state.isLoadingAcademicWeeks = false;
                state.academicWeeks = state.academicWeeks.filter(
                    (week) => week.id !== action.payload
                );
            })
            .addCase(DeleteAcademicWeeks.rejected, (state) => {
                state.isLoadingAcademicWeeks = false;
            });
    },
});

export const {
    setAcademicWeeks,
    setSelectedAcademicWeeksIDs,
    deleteAcademicWeek,
    updateAcademicWeek,
    setIsViewAcademicWeeksRestore,
    addAcademicWeek,
    toggleAcademicWeekActive,
} = academicWeekSlice.actions;

export default academicWeekSlice;
